import React, { useState, useEffect } from 'react';
import { getAuthAxios } from '@/store';
import { useI18n } from '@/components/I18nProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, CheckCircle, XCircle, RefreshCw, Loader2, Clock, User, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSecurityPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { t } = useI18n();

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const api = getAuthAxios();
      const params = { limit: 100 };
      if (filter === 'success') params.success_only = true;
      if (filter === 'failed') params.success_only = false;
      const res = await api.get('/admin/login-logs', { params });
      setLogs(res.data);
    } catch (err) {
      toast.error('Loglar yüklenemedi');
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [filter]);

  const formatDate = (ts) => {
    try {
      return new Date(ts).toLocaleString('tr-TR');
    } catch {
      return ts;
    }
  };

  const failedCount = logs.filter(l => !l.success).length;
  const successCount = logs.filter(l => l.success).length;

  return (
    <div className="space-y-3 sm:space-y-6" data-testid="admin-security-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          {t('securityLogsTitle')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{t('securityLogsDesc')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="total-attempts-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Clock className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-sm text-muted-foreground">{t('totalAttempts')}</p>
              <p className="text-2xl font-bold">{logs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="success-attempts-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle className="h-5 w-5 text-green-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">{t('successful')}</p>
              <p className="text-2xl font-bold text-green-600">{successCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="failed-attempts-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10"><XCircle className="h-5 w-5 text-[#1a224f]" /></div>
            <div>
              <p className="text-sm text-muted-foreground">{t('failed')}</p>
              <p className="text-2xl font-bold text-[#1a224f]">{failedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-lg">{t('loginAttempts')}</CardTitle>
            <CardDescription>{t('last100Attempts')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter} data-testid="log-filter">
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="success">{t('successful')}</SelectItem>
                <SelectItem value="failed">{t('failed')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchLogs} disabled={loading} data-testid="refresh-logs-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('noLogsYet')}</p>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-gray-500">#</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Email</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Tarih</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">IP</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Sebep</th>
                      <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-28">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={log.id || i} className={`border-b border-gray-100 transition-colors ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`login-log-${i}`}>
                        <td className="px-2 py-2 text-center text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span className="font-medium text-gray-900 truncate">{log.email}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                        <td className="px-3 py-2 text-gray-500">{log.ip ? <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3 text-gray-400" />{log.ip}</span> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{log.reason ? (log.reason === 'wrong_password' ? t('wrongPassword') : t('userNotFound')) : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2 text-center">
                          {log.success ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />{t('successful')}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" />{t('failed')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-2 max-h-[500px] overflow-y-auto">
                {logs.map((log, i) => (
                  <div key={log.id || i} className={`flex items-center justify-between p-3 rounded-lg border ${log.success ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`} data-testid={`login-log-card-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {log.success ? <CheckCircle className="h-4 w-4 text-green-600 shrink-0" /> : <XCircle className="h-4 w-4 text-[#1a224f] shrink-0" />}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate flex items-center gap-1"><User className="h-3 w-3" />{log.email}</span>
                          {log.reason && <Badge variant="destructive" className="text-xs">{log.reason === 'wrong_password' ? t('wrongPassword') : t('userNotFound')}</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(log.timestamp)}</span>
                          {log.ip && <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{log.ip}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge variant={log.success ? 'default' : 'destructive'} className="shrink-0 ml-2">{log.success ? t('successful') : t('failed')}</Badge>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
